import React, { useState, useEffect, useRef } from "react";
import {
  Box, CssBaseline, Drawer, List, ListItemButton, ListItemText,
  ListItemIcon, Collapse, Divider, IconButton, AppBar, Toolbar,
  useMediaQuery, useTheme, Tooltip, Typography, Paper, ClickAwayListener,
} from '@mui/material';
import {
  ExpandLess, ExpandMore,
  Dashboard as DashboardIcon,
  LibraryMusic, Inventory2 as InventoryIcon, People, Campaign, Settings,
  Menu as MenuIcon, ChevronLeft, ChevronRight, Search as SearchIcon,
  AccountCircle, Logout, Hub, Assessment,
  ClosedCaption, AutoAwesome,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import useScrollToTopOnNavigate from '../hooks/useScrollToTopOnNavigate';
import RouteConfig from '../constants/RouteConfig';
import SessionHelper from '../components/Helper/SessionHelper';
import UserInfo from "../components/User/UserInfo";
import { useRedesignFlag } from '../utils/redesignFlag';

const DRAWER_WIDTH = 260;
const MINI_DRAWER_WIDTH = 64;

const MENU_GROUPS = [
  { key: 'Content', label: 'Content', icon: <LibraryMusic /> },
  { key: 'Content Assets', label: 'Content Assets', icon: <InventoryIcon /> },
  { key: 'Captions', label: 'Captions', icon: <ClosedCaption /> },
  { key: 'Users', label: 'Users', icon: <People /> },
  { key: 'Reports', label: 'Reports', icon: <Assessment /> },
  { key: 'Engagement & Promotions', label: 'Engagement & Promotions', icon: <Campaign /> },
  { key: 'Settings & Admin', label: 'Settings & Admin', icon: <Settings /> },
  { key: 'Integrations', label: 'Integrations', icon: <Hub /> },
];

const TOP_ICONS = {
  '/dashboard': <DashboardIcon />,
  '/searches': <SearchIcon />,
};

const BOTTOM_ICONS = {
  '/my-account': <AccountCircle />,
  '/logout': <Logout />,
};

/* Redesign switch row at the top of the side menu (below UserInfo)
 * — a single-action call-to-action, not a stateful toggle. The whole
 * UI rerenders into the V2 chrome the moment it's clicked, so a
 * toggle pill would just disappear with the row that owned it. The
 * directional copy reads more honestly than "Use v2 redesign +
 * [Switch]". Same useRedesignFlag hook + localStorage key as the V2
 * TopBar dropdown — flipping here, flipping back from there. */
function RedesignToggleItem({ mini }) {
  const [v2, setV2] = useRedesignFlag();
  const label = v2
    ? 'Switch back to the old design'
    : 'Switch to the new design!';
  if (mini) {
    return (
      <Tooltip title={label} placement="right">
        <ListItemButton
          onClick={() => setV2(!v2)}
          sx={{ justifyContent: 'center', px: 0 }}
        >
          <ListItemIcon sx={{ minWidth: 0, color: 'primary.main' }}>
            <AutoAwesome />
          </ListItemIcon>
        </ListItemButton>
      </Tooltip>
    );
  }
  return (
    <ListItemButton onClick={() => setV2(!v2)}>
      <ListItemIcon sx={{ color: 'primary.main' }}><AutoAwesome /></ListItemIcon>
      <ListItemText
        primary={label}
        primaryTypographyProps={{ fontWeight: 600, color: 'primary.main' }}
      />
    </ListItemButton>
  );
}


function AuthLayout({ children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isNarrow = useMediaQuery(theme.breakpoints.down('lg'));
  const location = useLocation();
  const scrollRef = useRef(null);
  // Open every page at the top (the router keeps the previous page's scroll offset otherwise).
  useScrollToTopOnNavigate();

  const [drawerOpen, setDrawerOpen] = useState(!isNarrow);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Flyout state for mini mode
  const [flyoutGroup, setFlyoutGroup] = useState(null);
  const [flyoutTop, setFlyoutTop] = useState(0);

  useEffect(() => {
    setDrawerOpen(!isNarrow);
    setFlyoutGroup(null);
  }, [isNarrow]);

  useEffect(() => {
    if (isMobile) setMobileDrawerOpen(false);
  }, [location.pathname, isMobile]);

  // Close flyout on navigation
  useEffect(() => {
    setFlyoutGroup(null);
  }, [location.pathname]);

  const navRoutes = RouteConfig
    .filter(route => route.label && SessionHelper.hasPermission(route.requiredPermissions))
    .sort((a, b) => a.order - b.order);

  const ungroupedBottom = navRoutes.filter(r => !r.group && r.order >= 50);
  const grouped = MENU_GROUPS.map(g => ({
    ...g,
    items: navRoutes.filter(r => r.group === g.key),
  })).filter(g => g.items.length > 0);

  // Build a unified menu: standalone items + groups, sorted by order
  const standaloneItems = navRoutes.filter(r => !r.group && r.order < 50);
  const menuEntries = [
    ...standaloneItems.map(route => ({ type: 'item', route, order: route.order })),
    ...grouped.map(group => ({ type: 'group', group, order: Math.min(...group.items.map(i => i.order)) })),
  ].sort((a, b) => a.order - b.order);

  const isActive = (path) => location.pathname === path ||
    (path !== '/dashboard' && location.pathname.startsWith(path));

  const groupHasActive = (groupKey) =>
    navRoutes.some(r => r.group === groupKey && isActive(r.path));

  const findActiveGroup = () => {
    const active = navRoutes.find(r => isActive(r.path));
    return active?.group || null;
  };

  const activeGroup = findActiveGroup();

  const [openGroups, setOpenGroups] = useState(() => {
    return Object.fromEntries(MENU_GROUPS.map(g => [g.key, g.key === activeGroup]));
  });

  useEffect(() => {
    if (activeGroup) {
      setOpenGroups(prev => prev[activeGroup] ? prev : { ...prev, [activeGroup]: true });
    }
  }, [activeGroup]);

  const toggleGroup = (key) => {
    setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleFlyout = (e, groupKey) => {
    if (flyoutGroup === groupKey) {
      setFlyoutGroup(null);
    } else {
      setFlyoutTop(e.currentTarget.getBoundingClientRect().top);
      setFlyoutGroup(groupKey);
    }
  };

  // Scroll restoration
  useEffect(() => {
    const savedScrollPosition = sessionStorage.getItem("menuScrollPosition");
    if (savedScrollPosition && scrollRef.current) {
      scrollRef.current.scrollTop = parseInt(savedScrollPosition, 10);
    }
  }, [location, mobileDrawerOpen]);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollRef.current) {
        sessionStorage.setItem("menuScrollPosition", scrollRef.current.scrollTop);
      }
    };
    const currentScrollRef = scrollRef.current;
    if (currentScrollRef) {
      currentScrollRef.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (currentScrollRef) {
        currentScrollRef.removeEventListener('scroll', handleScroll);
      }
    };
  }, [drawerOpen, mobileDrawerOpen]);

  // Full sidebar content (expanded mode + mobile drawer)
  const fullDrawerContent = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1 }}>
        <Box sx={{ flex: 1, textAlign: 'center', pl: 5 }}>
          <img src="/img/top-ts-logo.jpg" alt="Company Logo" style={{ maxWidth: '70%', height: 'auto' }} />
        </Box>
        <IconButton onClick={() => isMobile ? setMobileDrawerOpen(false) : setDrawerOpen(false)} size="small">
          <ChevronLeft />
        </IconButton>
      </Box>
      <UserInfo user={SessionHelper.currentUser()} />
      <Divider />
      <Box ref={scrollRef} sx={{ flexGrow: 1, overflowY: 'auto' }}>
        <List component="nav" disablePadding>
          <RedesignToggleItem />
          <Divider sx={{ my: 0.5 }} />
          {menuEntries.map((entry) => entry.type === 'item' ? (
            <ListItemButton
              key={entry.route.path}
              component={Link}
              to={entry.route.path}
              selected={isActive(entry.route.path)}
            >
              <ListItemIcon sx={{ color: 'primary.main' }}>{TOP_ICONS[entry.route.path] || <DashboardIcon />}</ListItemIcon>
              <ListItemText primary={entry.route.label} primaryTypographyProps={{ fontWeight: 600, color: 'primary.main' }} />
            </ListItemButton>
          ) : (
            <React.Fragment key={entry.group.key}>
              <ListItemButton onClick={() => toggleGroup(entry.group.key)}>
                <ListItemIcon sx={{ color: 'primary.main' }}>{entry.group.icon}</ListItemIcon>
                <ListItemText primary={entry.group.label} primaryTypographyProps={{ fontWeight: 600, color: 'primary.main' }} />
                {openGroups[entry.group.key] ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={openGroups[entry.group.key]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {entry.group.items.map((route) => (
                    <ListItemButton
                      key={route.path}
                      component={Link}
                      to={route.path}
                      selected={isActive(route.path)}
                      sx={{ pl: 4 }}
                    >
                      <ListItemText primary={route.label} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </React.Fragment>
          ))}

          <Divider sx={{ my: 1 }} />

          {ungroupedBottom.map((route) => (
            <ListItemButton
              key={route.path}
              component={Link}
              to={route.path}
              selected={isActive(route.path)}
            >
              <ListItemIcon sx={{ color: 'primary.main' }}>{BOTTOM_ICONS[route.path]}</ListItemIcon>
              <ListItemText primary={route.label} primaryTypographyProps={{ fontWeight: 600, color: 'primary.main' }} />
            </ListItemButton>
          ))}
        </List>
      </Box>
    </>
  );

  // Mini sidebar: each group icon has a flyout rendered inline via absolute positioning
  const miniDrawerContent = (
    <ClickAwayListener onClickAway={() => setFlyoutGroup(null)}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ textAlign: 'center', py: 1 }}>
          <IconButton onClick={() => { setDrawerOpen(true); setFlyoutGroup(null); }} size="small">
            <ChevronRight />
          </IconButton>
        </Box>
        <Divider />
        <List component="nav" sx={{ flexGrow: 1 }}>
          <RedesignToggleItem mini />
          <Divider sx={{ my: 0.5 }} />
          {menuEntries.map((entry) => entry.type === 'item' ? (
            <Tooltip key={entry.route.path} title={entry.route.label} placement="right">
              <ListItemButton
                component={Link}
                to={entry.route.path}
                selected={isActive(entry.route.path)}
                onClick={() => setFlyoutGroup(null)}
                sx={{ justifyContent: 'center', px: 0 }}
              >
                <ListItemIcon sx={{ minWidth: 0, color: 'primary.main' }}>{TOP_ICONS[entry.route.path] || <DashboardIcon />}</ListItemIcon>
              </ListItemButton>
            </Tooltip>
          ) : (
            <Tooltip key={entry.group.key} title={flyoutGroup === entry.group.key ? '' : entry.group.label} placement="right">
              <ListItemButton
                onClick={(e) => toggleFlyout(e, entry.group.key)}
                selected={groupHasActive(entry.group.key)}
                sx={{ justifyContent: 'center', px: 0 }}
              >
                <ListItemIcon sx={{ minWidth: 0, color: 'primary.main' }}>{entry.group.icon}</ListItemIcon>
              </ListItemButton>
            </Tooltip>
          ))}

          <Divider sx={{ my: 1 }} />

          {ungroupedBottom.map((route) => (
            <Tooltip key={route.path} title={route.label} placement="right">
              <ListItemButton
                component={Link}
                to={route.path}
                selected={isActive(route.path)}
                onClick={() => setFlyoutGroup(null)}
                sx={{ justifyContent: 'center', px: 0 }}
              >
                <ListItemIcon sx={{ minWidth: 0, color: 'primary.main' }}>
                  {BOTTOM_ICONS[route.path] || <AccountCircle />}
                </ListItemIcon>
              </ListItemButton>
            </Tooltip>
          ))}
        </List>

        {/* Flyout menu — fixed position to escape drawer overflow */}
        {flyoutGroup && (() => {
          const group = grouped.find(g => g.key === flyoutGroup);
          if (!group) return null;
          return (
            <Paper
              elevation={8}
              sx={{
                position: 'fixed',
                left: MINI_DRAWER_WIDTH,
                top: flyoutTop,
                minWidth: 220,
                zIndex: theme.zIndex.modal,
                py: 0.5,
              }}
            >
              <Typography variant="subtitle2" sx={{ px: 2, pt: 1, pb: 0.5, color: 'text.secondary', fontWeight: 600 }}>
                {group.label}
              </Typography>
              <List dense disablePadding>
                {group.items.map((route) => (
                  <ListItemButton
                    key={route.path}
                    component={Link}
                    to={route.path}
                    selected={isActive(route.path)}
                    onClick={() => setFlyoutGroup(null)}
                  >
                    <ListItemText primary={route.label} />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          );
        })()}
      </Box>
    </ClickAwayListener>
  );

  // Mobile layout: AppBar + temporary drawer
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar position="fixed" color="default" elevation={1} sx={{ zIndex: theme.zIndex.drawer + 1 }}>
          <Toolbar variant="dense">
            <IconButton edge="start" onClick={() => setMobileDrawerOpen(true)} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
            <img src="/img/top-ts-logo.jpg" alt="Company Logo" style={{ height: 28 }} />
          </Toolbar>
        </AppBar>
        <Drawer
          variant="temporary"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          sx={{
            [`& .MuiDrawer-paper`]: {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
            },
          }}
        >
          {fullDrawerContent}
        </Drawer>
        <Box component="main" sx={{ flexGrow: 1, p: 3, pt: 9 }}>
          {children}
        </Box>
      </Box>
    );
  }

  // Desktop layout: permanent drawer (full or mini)
  const currentWidth = drawerOpen ? DRAWER_WIDTH : MINI_DRAWER_WIDTH;

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <Drawer
        variant="permanent"
        sx={{
          width: currentWidth,
          flexShrink: 0,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          [`& .MuiDrawer-paper`]: {
            width: currentWidth,
            boxSizing: 'border-box',
            borderRight: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            overflowX: 'hidden',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
      >
        {drawerOpen ? fullDrawerContent : miniDrawerContent}
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export default AuthLayout;
